import { FASTAPI_APP_BASE_URL, WORKER_API_URL } from './config';
import { getStoredToken } from '../stores/authStore';

type UpsertFeedbackRequest = {
  threadMessageId: string;
  threadId?: string;
  conversationRef?: string | number;
  rating: 'up' | 'down';
  chips?: string[];
  note?: string;
  requestId?: string;
};

export type FeedbackRow = {
  id: string;
  user_id: number;
  conversation_id: number;
  thread_message_id: string;
  thread_id: string;
  rating: 'up' | 'down';
  chips: string[];
  note?: string | null;
  request_id?: string | null;
};

const headers = () => {
  // Prefer Zustand store (authoritative in-app) and fall back to localStorage
  let raw: string | null = null;
  try { raw = getStoredToken() || null; } catch { raw = null; }
  if (!raw && typeof localStorage !== 'undefined') {
    try { raw = localStorage.getItem('token'); } catch { /* noop */ }
  }
  const token = raw ? (raw.startsWith('Bearer ') ? raw : `Bearer ${raw}`) : null;
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': token } : {}),
  } as Record<string, string>;
};

async function forwardJson(url: string, init: RequestInit) {
  const res = await fetch(url, init);
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(txt || `HTTP ${res.status}`);
  }
  return res.json();
}

export const feedbackService = {
  async upsert(req: UpsertFeedbackRequest): Promise<FeedbackRow> {
    const body = {
      thread_message_id: req.threadMessageId,
      thread_id: req.threadId,
      conversation_ref: req.conversationRef,
      rating: req.rating,
      chips: req.chips || [],
      note: req.note,
      request_id: req.requestId,
    } as any;
    const init: RequestInit = { method: 'POST', headers: headers(), body: JSON.stringify(body) };
    try {
      return await forwardJson(`${WORKER_API_URL}/api/v1/feedback/messages`, init);
    } catch {
      return await forwardJson(`${FASTAPI_APP_BASE_URL}/api/v1/feedback/messages`, init);
    }
  },

  async list(conversationRef: string | number, messageIds?: string[]): Promise<FeedbackRow[]> {
    const params = new URLSearchParams({ conversation_ref: String(conversationRef) });
    if (messageIds && messageIds.length) params.set('message_ids', messageIds.join(','));
    const init: RequestInit = { method: 'GET', headers: headers() };
    try {
      return await forwardJson(`${WORKER_API_URL}/api/v1/feedback/messages?${params.toString()}`, init);
    } catch {
      return await forwardJson(`${FASTAPI_APP_BASE_URL}/api/v1/feedback/messages?${params.toString()}`, init);
    }
  },

  async remove(id: string): Promise<{ success: boolean }> {
    const init: RequestInit = { method: 'DELETE', headers: headers() };
    try {
      return await forwardJson(`${WORKER_API_URL}/api/v1/feedback/messages/${id}`, init);
    } catch {
      return await forwardJson(`${FASTAPI_APP_BASE_URL}/api/v1/feedback/messages/${id}`, init);
    }
  },
};
